import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Copy, FileDown, Share2, MessageCircle, Mail, ArrowRight, Check, X } from "lucide-react";

const STATUSES = ["DRAFT", "SENT", "APPROVED", "REJECTED", "EXPIRED"];

const lineTotal = (line) => line.qty * line.unit_price * (1 - (line.discount_pct || 0) / 100);
const formatINR = (v) => `₹${(v || 0).toLocaleString()}`;

function PdfPreview({ q, onClose }) {
  if (!q) return null;
  const printNow = () => window.print();
  return (
    <Dialog open={!!q} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl print:!shadow-none print:!border-none">
        <div id="quote-print" className="space-y-4">
          <div className="border-b pb-3 flex items-start justify-between">
            <div>
              <div className="font-display text-2xl font-black">QUOTATION</div>
              <div className="font-mono text-xs text-muted-foreground">{q.number}</div>
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold">{q.customer_name}</div>
              {q.valid_until && <div className="text-xs text-muted-foreground">Valid until: {q.valid_until}</div>}
            </div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Unit price</TableHead><TableHead>Disc%</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {(q.lines || []).map((l, i) => (
                <TableRow key={i}><TableCell>{l.product}</TableCell><TableCell>{l.qty} {l.unit}</TableCell><TableCell>{formatINR(l.unit_price)}</TableCell><TableCell>{l.discount_pct || 0}</TableCell><TableCell className="text-right font-semibold">{formatINR(lineTotal(l))}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-right text-xl font-display font-black">Grand total: {formatINR(q.total)}</div>
          {q.notes && <div className="text-xs text-muted-foreground border-t pt-2">Notes: {q.notes}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={printNow} data-testid="quote-print"><FileDown className="w-4 h-4 mr-1" /> Print / Save PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Quotations() {
  const { user } = useAuth();
  const isManager = user?.role === "SALES_MANAGER" || user?.role === "ORG_ADMIN";
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ customer_id: "", valid_until: "", notes: "", lines: [{ product: "", qty: 1, unit: "box", unit_price: 0, discount_pct: 0 }] });

  const load = async () => {
    const params = tab === "ALL" ? {} : { status: tab };
    const { data } = await api.get("/sales/quotations", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/sales/customers").then(({ data }) => setCustomers(data)); }, []);

  const total = form.lines.reduce((s, l) => s + lineTotal(l), 0);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { product: "", qty: 1, unit: "box", unit_price: 0, discount_pct: 0 }] });
  const updLine = (i, key, val) => setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, [key]: key === "product" || key === "unit" ? val : parseFloat(val || "0") } : l) });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const save = async () => {
    try { await api.post("/sales/quotations", form); toast.success("Quotation created"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const transition = async (q, status) => {
    try { await api.post(`/sales/quotations/${q.id}/transition`, null, { params: { status } }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const duplicate = async (q) => { try { await api.post(`/sales/quotations/${q.id}/duplicate`); toast.success("Duplicated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const convertOrder = async (q) => { try { const { data } = await api.post(`/sales/quotations/${q.id}/convert-to-order`); toast.success(`Order ${data.number} created`); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const shareLink = (q, channel) => {
    const url = `${window.location.origin}/sales/quotations?id=${q.id}`;
    const text = `Quotation ${q.number} — total ${formatINR(q.total)} — ${url}`;
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
    else if (channel === "email") window.open(`mailto:?subject=Quotation ${q.number}&body=${encodeURIComponent(text)}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-quotations">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Quotations</h1>
          <p className="text-muted-foreground">Draft → send → approve → convert to order. Share PDF over WhatsApp or email in one click.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="quote-create-btn"><Plus className="w-4 h-4 mr-1" /> New quote</Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ALL" data-testid="quote-tab-ALL">All</TabsTrigger>
          {STATUSES.map((s) => <TabsTrigger key={s} value={s} data-testid={`quote-tab-${s}`}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Number</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((q) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="font-mono text-xs font-semibold">{q.number}</TableCell>
                <TableCell>{q.customer_name}</TableCell>
                <TableCell>{formatINR(q.total)}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{q.status}</Badge></TableCell>
                <TableCell className="font-mono text-[10px]">{new Date(q.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setPreview(q)} title="Preview/PDF" data-testid={`quote-pdf-${q.id}`}><FileDown className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => shareLink(q, "whatsapp")} title="WhatsApp"><MessageCircle className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => shareLink(q, "email")} title="Email"><Mail className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => duplicate(q)} title="Duplicate"><Copy className="w-4 h-4" /></Button>
                  {q.status === "DRAFT" && <Button size="sm" onClick={() => transition(q, "SENT")} data-testid={`quote-send-${q.id}`}><Share2 className="w-4 h-4 mr-1" />Send</Button>}
                  {q.status === "SENT" && isManager && <Button size="sm" onClick={() => transition(q, "APPROVED")} data-testid={`quote-approve-${q.id}`}><Check className="w-4 h-4" /></Button>}
                  {q.status === "SENT" && isManager && <Button size="sm" variant="outline" onClick={() => transition(q, "REJECTED")}><X className="w-4 h-4" /></Button>}
                  {q.status === "APPROVED" && <Button size="sm" onClick={() => convertOrder(q)} data-testid={`quote-convert-${q.id}`}><ArrowRight className="w-4 h-4 mr-1" />Order</Button>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No quotations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>New quotation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="quote-customer"><SelectValue placeholder="Pick customer…" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.kind})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="quote-valid" /></div>
            </div>
            <div className="space-y-2">
              <Label>Line items</Label>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-4" placeholder="Product" value={l.product} onChange={(e) => updLine(i, "product", e.target.value)} data-testid={`quote-line-${i}-product`} />
                  <Input className="col-span-2" type="number" placeholder="Qty" value={l.qty} onChange={(e) => updLine(i, "qty", e.target.value)} data-testid={`quote-line-${i}-qty`} />
                  <Input className="col-span-2" placeholder="Unit" value={l.unit} onChange={(e) => updLine(i, "unit", e.target.value)} />
                  <Input className="col-span-2" type="number" placeholder="₹/unit" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} data-testid={`quote-line-${i}-price`} />
                  <Input className="col-span-1" type="number" placeholder="%" value={l.discount_pct} onChange={(e) => updLine(i, "discount_pct", e.target.value)} />
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => rmLine(i)}><X className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addLine} data-testid="quote-add-line"><Plus className="w-4 h-4 mr-1" /> Add line</Button>
              <div className="text-right font-semibold">Total: {formatINR(total)}</div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={!form.customer_id} data-testid="quote-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreview q={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
